<template>
  <div class="border border-slate-200 bg-white shadow-sm">
    <div class="border-b border-slate-200 bg-slate-50 px-4 py-3">
      <h2 class="text-base font-semibold text-slate-800">Setpoints do alarme</h2>
      <p class="mt-0.5 text-sm text-slate-600">
        Parâmetros usados no sensor para geração deste tipo de alarme.
      </p>
    </div>
    <div class="p-4">
      <p v-if="isLoading" class="text-sm text-slate-500">Carregando setpoints...</p>
      <p v-else-if="!profileId && props.alarmType == 'correlational'" class="text-sm text-slate-500">
        Alarmes correlacionais são baseados nos alarmes dos sensores.
      </p>
      <p v-else-if="!profileId" class="text-sm text-slate-500">
        Nenhum perfil de alarme configurado para este tipo no sensor.
      </p>
      <p v-else-if="errorMessage" class="text-sm text-red-600">{{ errorMessage }}</p>
      <div v-else-if="setpoints && Object.keys(setpoints).length > 0" class="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        <div
          v-for="(value, key) in setpoints"
          :key="key"
          class="border border-slate-200 bg-slate-50/50 p-3">
          <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">{{ formatLabel(key) }}</p>
          <p class="mt-1 font-medium text-slate-900">{{ formatValue(key, value) }}</p>
        </div>
      </div>
      <p v-else class="text-sm text-slate-500">Nenhum setpoint disponível.</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import AlarmResource from '@/api/resources/alarm'
import SensorResource from '@/api/resources/sensor'
import { formatOutsideRegularUtcTimeToLocalHHmm } from '@/utils/date/date-time'
import { computed, onMounted, ref, watch } from 'vue'

const props = defineProps<{
  sensorId: number
  sensorType: 'electricity' | 'temperature' | 'door_open'
  alarmType: string
}>()

const isLoading = ref(false)
const errorMessage = ref('')
const sensorDetail = ref<Record<string, any> | null>(null)
const profileSettings = ref<Record<string, number | string> | null>(null)

const profileId = computed(() => {
  if (!sensorDetail.value) return null
  const s = sensorDetail.value
  if (props.sensorType === 'temperature' && props.alarmType === 'critical_temperature') {
    return s.alarm_critical_temperature_sensor_id ?? null
  }
  if (props.sensorType === 'electricity' && props.alarmType === 'interruption') {
    return s.alarm_interrupt_sensor_id ?? null
  }
  if (props.sensorType === 'electricity' && props.alarmType === 'low_load') {
    return s.alarm_low_load_sensor_id ?? null
  }
  if (props.sensorType === 'electricity' && props.alarmType === 'power_factor') {
    return s.alarm_power_factor_sensor_id ?? null
  }
  if (props.sensorType === 'electricity' && props.alarmType === 'overvoltage') {
    return s.alarm_overvoltage_sensor_id ?? null
  }
  if (props.sensorType === 'electricity' && props.alarmType === 'defrost_time') {
    return s.alarm_defrost_sensor_id ?? null
  }
  if (props.sensorType === 'electricity' && props.alarmType === 'burnt_resistor') {
    return s.alarm_defrost_sensor_id ?? null
  }
  if (props.sensorType === 'electricity' && props.alarmType === 'activation_peak_hour') {
    return s.alarm_peak_hour_sensor_id ?? null
  }
  if (props.sensorType === 'electricity' && props.alarmType === 'empty_mixer') {
    return s.alarm_empty_mixer_sensor_id ?? null
  }
  if (props.sensorType === 'electricity' && props.alarmType === 'outside_regular_time') {
    return s.alarm_outside_regular_sensor_id ?? null
  }
  if (props.alarmType === 'no_data') {
    return s.alarm_no_data_sensor_id ?? null
  }
  if (
    (props.sensorType === 'electricity' || props.sensorType === 'door_open') &&
    props.alarmType === 'door_open'
  ) {
    return s.alarm_door_open_sensor_id ?? null
  }
  if (props.sensorType === 'electricity' && props.alarmType === 'out_of_cycle') {
    return s.alarm_out_of_cycle_sensor_id ?? null
  }
  return null
})

const setpoints = computed(() => profileSettings.value ?? null)

const PROFILE_GETTERS: Record<string, (id: number) => Promise<any>> = {
  critical_temperature: (id) => AlarmResource.getAlarmCriticalTemperatureSensorProfile(id),
  interruption: (id) => AlarmResource.getAlarmInterruptSensorProfile(id),
  low_load: (id) => AlarmResource.getAlarmLowLoadSensorProfile(id),
  power_factor: (id) => AlarmResource.getAlarmPowerFactorSensorProfile(id),
  defrost_time: (id) => AlarmResource.getAlarmDefrostSensorProfile(id),
  burnt_resistor: (id) => AlarmResource.getAlarmDefrostSensorProfile(id),
  activation_peak_hour: (id) => AlarmResource.getAlarmPeakHourSensorProfile(id),
  empty_mixer: (id) => AlarmResource.getAlarmEmptyMixerSensorProfile(id),
  outside_regular_time: (id) => AlarmResource.getAlarmOutsideRegularSensorProfile(id),
  no_data: (id) => AlarmResource.getAlarmNoDataSensorProfile(id),
  door_open: (id) => AlarmResource.getAlarmDoorOpenSensorProfile(id),
  out_of_cycle: (id) => AlarmResource.getAlarmOutOfCycleSensorProfile(id),
  overvoltage: (id) => AlarmResource.getAlarmOvervoltageSensorProfile(id),
}

function formatLabel(key: string): string {
  const labels: Record<string, string> = {
    interrupt_current_setpoint: 'Corrente de interrupção (A)',
    interrupt_time_setpoint: 'Tempo de interrupção (min)',
    debouncing_time_setpoint: 'Tempo de debounce (min)',
    temperature_setpoint: 'Temperatura crítica (°C)',
    temperature_duration_setpoint: 'Duração (min)',
    temperature_debounce_duration_setpoint: 'Debounce (min)',
    low_load_min_current_setpoint: 'Corrente mínima (A)',
    low_load_max_current_setpoint: 'Corrente máxima (A)',
    low_load_duration_setpoint: 'Duração (min)',
    low_load_debounce_duration_setpoint: 'Debounce (min)',
    power_factor_setpoint: 'Fator de potência',
    power_factor_alert_sequence: 'Sequência de alerta',
    defrost_min_current_setpoint: 'Corrente mín. degelo (A)',
    defrost_max_current_setpoint: 'Corrente máx. degelo (A)',
    defrost_current_ratio_setpoint_max: 'Razão corrente máx.',
    defrost_duration_setpoint: 'Duração degelo (min)',
    defrost_duration_error_margin: 'Margem erro duração',
    defrost_current_ratio_setpoint_min: 'Razão corrente mín.',
    defrost_debounce_duration_setpoint: 'Debounce (min)',
    peak_hour_current_threshold: 'Limiar corrente (A)',
    peak_hour_start_time: 'Início horário de pico',
    peak_hour_duration_minutes: 'Duração (min)',
    peak_hour_min_percentage_interrupt: '% mín. interrupção',
    empty_mixer_multiplier: 'Multiplicador',
    empty_mixer_variation_threshold: 'Limiar variação',
    empty_mixer_activation_setpoint: 'Ativação (A)',
    empty_mixer_min_peak_duration_minutes: 'Duração mín. pico (min)',
    empty_mixer_time_limit_minutes: 'Limite tempo (min)',
    outside_regular_time_setpoint: 'Setpoint',
    outside_regular_time_duration_setpoint: 'Duração (min)',
    outside_regular_time_debounce_duration_setpoint: 'Debounce (min)',
    outside_regular_time_start_time: 'Horário início',
    outside_regular_time_end_time: 'Horário fim',
    no_data_duration_setpoint: 'Duração sem dados (min)',
    max_door_open_time_setpoint: 'Tempo máx. porta aberta (min)',
    debounce_time_setpoint: 'Debounce (min)',
    out_of_cycle_time_setpoint: 'Tempo fora de ciclo (min)',
    out_of_cycle_high_current_setpoint: 'Corrente alta (A)',
    out_of_cycle_low_current_setpoint: 'Corrente baixa (A)',
    overvoltage_duration_setpoint: 'Duração de sobretensão (min)',
    overvoltage_debounce_duration_setpoint: 'Debounce (min)',
  }
  return labels[key] ?? key.replace(/_/g, ' ')
}

function formatValue(key: string, value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'number') return String(Number(value.toFixed(2)))
  if (
    (key === 'outside_regular_time_start_time' ||
      key === 'outside_regular_time_end_time' ||
      key === 'peak_hour_start_time') &&
    typeof value === 'string'
  ) {
    return formatOutsideRegularUtcTimeToLocalHHmm(value) || String(value)
  }
  return String(value)
}

async function fetchSensor() {
  if (!props.sensorId) return
  isLoading.value = true
  errorMessage.value = ''
  try {
    if (props.sensorType === 'temperature') {
      sensorDetail.value = await SensorResource.getTemperatureSensorsById(props.sensorId)
    } else if (props.sensorType === 'door_open') {
      sensorDetail.value = await SensorResource.getOpenSensorsById(props.sensorId)
    } else {
      sensorDetail.value = await SensorResource.getEnergySensorsById(props.sensorId)
    }
  } catch {
    sensorDetail.value = null
    errorMessage.value = 'Não foi possível carregar dados do sensor.'
  } finally {
    isLoading.value = false
  }
}

async function fetchProfile() {
  const id = profileId.value
  const getter = PROFILE_GETTERS[props.alarmType]
  if (!id || !getter) {
    profileSettings.value = null
    return
  }
  isLoading.value = true
  errorMessage.value = ''
  try {
    const res = await getter(id)
    profileSettings.value = res?.settings ?? res ?? null
  } catch {
    profileSettings.value = null
    errorMessage.value = 'Não foi possível carregar setpoints.'
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  fetchSensor()
})

watch(
  profileId,
  (id) => {
    if (id && PROFILE_GETTERS[props.alarmType]) fetchProfile()
    else profileSettings.value = null
  },
  { immediate: true },
)

watch(
  () => [props.sensorId, props.sensorType, props.alarmType],
  () => {
    fetchSensor()
  },
)
</script>
